<template>
  <div id="notes" class="lg:flex">
    <sidebar>
      <div class="sidebar-menu-wrapper">
        <div class="pt-3 folder">
          <router-link to="/app/notes/all" class="block py-1.5 pr-2.5 pl-6" :class="{ 'router-link-exact-active': $route.params.folderId === 'all' }">
            All Notes <span class="small-counter">{{ counterDisplay(notes.filter(note => !note.archived).length)}}</span>
          </router-link>
        </div>
        <div class="folder" v-for="folder in folders.slice(0, 1)" :key="folder._id">
          <router-link :to="'/app/notes/' + folder._id" class="block py-1.5 pr-2.5 pl-6" :class="{ 'router-link-exact-active': $route.params.folderId === folder._id }">
            <span>{{ folder.title }}</span>
            <span class="small-counter">{{
              counterDisplay(notes.filter(note => !note.archived && (note.folderId === folder._id)).length)
            }}</span>
          </router-link>
        </div>
        <div class="folders-title">Folders</div>
        <folders-draggable
          :folders="parentFolders"
          :count="countNotesInFolder"
          :link="'notes'"
        >
        </folders-draggable>
        <folder-form></folder-form>
        <div class="pb-6 mt-auto folder"><router-link to="/app/notes/archived">Archived</router-link></div>
      </div>
    </sidebar>
    <div class="grid overflow-hidden p-0 w-full transition-all duration-200 ease-out grid-cols-notes lg:grid-cols-notes-lg lg:flex-grow" :class="{ 'lg:-ml-60': openSidebar }">
      <note-list
        @create-note="createNoteBlank"
        @create-note-template="createNoteTemplate"
        @select-note="viewNote"
        @delete-all-archived="deleteAllArchivedNotes"
        :loading="notesLoading"
        :selectedFolder="selectedFolder"
        :selectedNoteId="selectedNoteId"
        :filteredNotes="filteredNotes"
        :showNoteListDesktop="showNoteListDesktop"
        :showNoteListMobile="showNoteListMobile"/>
      <div class="relative z-20 bg-white transition-all duration-200 transform note-container" :class="[ showNoteListMobile ? 'translate-x-0' : '-translate-x-full lg:translate-x-0', showNoteListDesktop ? 'lg:ml-0' : 'lg:-ml-90 ease-out']">
        <div v-if="notesLoading" class="h-full">
          <Loading/>
        </div>
        <div v-else-if="!selectedNoteId" class="flex justify-center items-center h-full text-gray-300">
          <span>Select or <a class="pb-px border-b border-gray-400 hover:border-black hover:text-black" @click.prevent="createNoteBlank">create a note</a> to start editing</span>
        </div>
        <div v-else class="relative py-12 h-screen lg:overflow-y-auto">
          <note
            @toggle-desktop-full="toggleDesktopFull"
            @toggle-note-list-container="toggleNoteListContainer"
            @view-note="viewNote"
            :openSidebar="openSidebar"
            :folders="sortedFolders"
            :showNoteListDesktop="showNoteListDesktop"
            :selectedNoteId="selectedNoteId"
            :note="selectedNote"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { makeFindMixin } from 'feathers-vuex'
import { differenceInSeconds, parseISO } from 'date-fns'
import { getNoteTemplate } from '../../utils'

import Folders from '@/mixins/Folders'
import Sidebar from '@/mixins/Sidebar'
import { NOTE_LIST_FIELDS, ensureContent } from '@/lib/notes'

import Loading from '@/components/Loading'
import Note from '@/components/Note'
import NoteList from '@/components/NoteList'

export default {
  name: 'Notes',
  mixins: [
    Folders,
    Sidebar,
    makeFindMixin({ service: 'notes', watch: true })
  ],
  components: { Note, NoteList, Loading },
  data() {
    return {
      showNoteListDesktop: true,
      showNoteListMobile: true,
      newNote: {
        title: 'Untitled Note',
        content: '',
        folderId: ''
      }
    }
  },
  computed: {
    ...mapGetters(['connected']),
    ...mapGetters('notes', { getNoteInStore: 'get' }),
    // The list is fetched with a $select, and feathers-vuex re-applies that
    // $select when reading back out of the store, so every record in `notes`
    // has been projected down to the list fields -- no content, whatever the
    // store holds. Reading the selected note straight from the store is what
    // gives Note.vue the record its body is merged into.
    selectedNote() {
      return this.selectedNoteId ? this.getNoteInStore(this.selectedNoteId) : undefined
    },
    // $select: the list renders titles, dates and an excerpt, never a body.
    // Asking for bodies here is what made this view take minutes to appear.
    notesParams() {
      return {
        query: { $sort: { updatedAt: -1 }, $select: NOTE_LIST_FIELDS },
        temps: !this.connected
      }
    },
    notesQueryWhen() { return this.connected },
    notesLoading() { return this.isFindNotesPending && !this.haveNotesBeenRequestedOnce },
    selectedNoteId: {
      get() {
        return this.$store.state.selectedNoteId
      },
      set(noteId) {
        this.$store.commit('setSelectedNoteId', noteId)
      }
    },
    filteredNotes() {
      let sortedNotes
      if (this.$route.params.folderId === 'all') {
        sortedNotes = this.notes.filter(note => !note.archived)
      } else if (this.$route.params.folderId === 'archived') {
        sortedNotes = this.notes.filter(note => note.archived)
      } else {
        sortedNotes = this.notes.filter(note => !note.archived && note.folderId === this.$route.params.folderId)
      }
      return sortedNotes.sort((left, right) => {
        return differenceInSeconds(parseISO(right.updatedAt), parseISO(left.updatedAt))
      })
    }
  },
  watch: {

    '$route' (to, from) {
      if (this.$route.params.noteId) {
        this.viewNote(this.$route.params.noteId)
      }
    },
    '$route.meta' ({ showNoteList }) {
      this.showNoteListMobile = showNoteList
    }
  },
  mounted() {
    if (this.notes.length > 0 && (!this.selectedNoteId || !this.notes.find(note => note._id === this.selectedNoteId))) {
      this.selectedNoteId = this.notes.filter(note => !note.archived)[0]._id
    }
    if (this.$route.params.noteId) {
      this.viewNote(this.$route.params.noteId)
    }
  },
  methods: {
    countNotesInFolder(folderId) {
      return this.notes.filter(note => !note.archived && note.folderId === folderId).length
    },
    counterDisplay(num) {
      return num > 0 ? num > 999 ? '999+' : num : 0
    },
    createNote(title, template) {
      if (this.$route.params.folderId === 'all') {
        this.$set(this.newNote, 'folderId', this.folders[0]._id)
      } else {
        this.$set(this.newNote, 'folderId', this.selectedFolder._id)
      }
      this.$set(this.newNote, 'title', title)
      this.$set(this.newNote, 'content', template)
      if (title === 'Daily Journal') {
        this.$set(this.newNote, 'endDate', new Date())
      }
      const { Note } = this.$FeathersVuex.api
      const note = new Note(this.newNote)
      note.save().then(createdNote => {
        this.viewNote(createdNote._id)
        this.newNote = {
          title: 'Untitled Note',
          content: '',
          folderId: ''
        }
      })
    },
    createNoteBlank() {
      this.createNote('Untitled Note', '')
    },
    createNoteTemplate(type) {
      switch (type) {
        case 'meeting':
          this.createNote('Meeting Note', getNoteTemplate(type))
          break
        case 'journal':
          this.createNote('Daily Journal', getNoteTemplate(type))
          break
        case 'project':
          this.createNote('Project Plan', getNoteTemplate(type))
          break
      }
    },
    viewNote(noteId) {
      this.selectedNoteId = noteId
      // The list carries no bodies, so opening a note is what fetches one.
      // Deliberately not awaited: the header, folder and dates render from
      // metadata straight away and the editor fills in when the body lands.
      const { Note } = this.$FeathersVuex.api
      ensureContent(Note, noteId).catch(e => console.log('note content failed', e))
      if (this.$route.params.noteId !== noteId) {
        this.$router.push({ path: `/app/notes/${this.$route.params.folderId}/${noteId}` })
      }
      this.toggleNoteListContainer(false)
      this.$el.querySelector('.note-container > div').scrollTop = 0
    },
    toggleNoteListContainer(bool) {
      this.showNoteListMobile = bool
    },
    toggleDesktopFull(bool) {
      if (this.showNoteListDesktop === true && this.openSidebar === true) {
        this.showNoteListDesktop = false
        this.$store.commit('toggleSidebar', true)
      } else {
        this.showNoteListDesktop = !bool
        if (this.openSidebar !== bool) { this.$store.commit('toggleSidebar', bool) }
      }
    },
    deleteAllArchivedNotes() {
      const result = confirm('Are you sure want to permanently DELETE all archived notes?')
      if (result) {
        this.$store.dispatch('notes/find', { query: { archived: true } })
          .then(note => {
            note.forEach(note => { note.remove() })
          }).catch(err => console.error(err))
      }
    }
  }
}
</script>
